import type { CurriculumTierId, MiniCheckpoint } from '../types';
import { filterWords } from './curriculum/generators';
import { LEVEL_6_VALID } from './curriculum/grundniveau';
import { NIVEAU_1_SPRINT_WORDS, NIVEAU_1_UNLOCKED_KEYS } from './curriculum/niveau-1';
import { NIVEAU_2_SPRINT_WORDS, NIVEAU_2_UNLOCKED_KEYS } from './curriculum/niveau-2';
import { NIVEAU_3_SPRINT_WORDS, NIVEAU_3_UNLOCKED_KEYS } from './curriculum/niveau-3';
import { NIVEAU_4_ALPHABET_WORDS, NIVEAU_4_UNLOCKED_KEYS } from './curriculum/niveau-4';
import { NIVEAU_5_SPRINT_WORDS, NIVEAU_5_UNLOCKED_KEYS } from './curriculum/niveau-5';
import { NIVEAU_6_SPRINT_WORDS, NIVEAU_6_UNLOCKED_KEYS } from './curriculum/niveau-6';

const GN_KEYS = [...NIVEAU_1_UNLOCKED_KEYS].slice(0, 9); // hjemmerække uden mellemrum for trin 3

export const MINI_CHECKPOINTS: MiniCheckpoint[] = [
  {
    id: 'mini-after-grundniveau-trin-3',
    afterTrinId: 'grundniveau-trin-3',
    tierId: 'grundniveau',
    title: 'Kontrol: ringfingrene',
    description: '45 sek — vis at du kan S, L, D og K uden at tøve.',
    goal: 'Bestå med mindst 90% nøjagtighed og 5 ord.',
    unlockedKeys: ['a', 's', 'd', 'f', 'j', 'k', 'l', 'æ'],
    sprintWords: filterWords(LEVEL_6_VALID, ['a', 's', 'd', 'f', 'j', 'k', 'l', 'æ']),
    durationMs: 45_000,
    minWords: 5,
    minAccuracy: 0.9,
  },
  {
    id: 'mini-after-grundniveau-trin-6',
    afterTrinId: 'grundniveau-trin-6',
    tierId: 'grundniveau',
    title: 'Kontrol: grundniveau',
    description: '45 sek — korte ord fra hele grundniveau.',
    goal: 'Bestå med mindst 90% nøjagtighed og 5 ord.',
    unlockedKeys: [...GN_KEYS, ' '],
    sprintWords: LEVEL_6_VALID,
    durationMs: 45_000,
    minWords: 5,
    minAccuracy: 0.9,
  },
  {
    id: 'mini-after-niveau-1-trin-2',
    afterTrinId: 'niveau-1-trin-2',
    tierId: 'niveau-1',
    title: 'Kontrol: hjemmerækkens midte',
    description: '45 sek med G, H og det du har lært.',
    goal: 'Bestå med mindst 90% nøjagtighed og 5 ord.',
    unlockedKeys: NIVEAU_1_UNLOCKED_KEYS,
    sprintWords: NIVEAU_1_SPRINT_WORDS,
    durationMs: 45_000,
    minWords: 5,
    minAccuracy: 0.9,
  },
  {
    id: 'mini-after-niveau-2-trin-3',
    afterTrinId: 'niveau-2-trin-3',
    tierId: 'niveau-2',
    title: 'Kontrol: V og B',
    description: '45 sek — ord med de nye bogstaver.',
    goal: 'Bestå med mindst 90% nøjagtighed og 5 ord.',
    unlockedKeys: NIVEAU_2_UNLOCKED_KEYS,
    sprintWords: NIVEAU_2_SPRINT_WORDS,
    durationMs: 45_000,
    minWords: 5,
    minAccuracy: 0.9,
  },
  {
    id: 'mini-after-niveau-3-trin-3',
    afterTrinId: 'niveau-3-trin-3',
    tierId: 'niveau-3',
    title: 'Kontrol: Q og P',
    description: '45 sek — ord fra øverste række.',
    goal: 'Bestå med mindst 90% nøjagtighed og 5 ord.',
    unlockedKeys: NIVEAU_3_UNLOCKED_KEYS,
    sprintWords: NIVEAU_3_SPRINT_WORDS,
    durationMs: 45_000,
    minWords: 5,
    minAccuracy: 0.9,
  },
  {
    id: 'mini-after-niveau-4-trin-3',
    afterTrinId: 'niveau-4-trin-3',
    tierId: 'niveau-4',
    title: 'Kontrol: tegn og bogstaver',
    description: '45 sek — ord med komma, punktum og bindestreg.',
    goal: 'Bestå med mindst 90% nøjagtighed og 5 ord.',
    unlockedKeys: NIVEAU_4_UNLOCKED_KEYS,
    sprintWords: NIVEAU_4_ALPHABET_WORDS,
    durationMs: 45_000,
    minWords: 5,
    minAccuracy: 0.9,
  },
  {
    id: 'mini-after-niveau-5-trin-5',
    afterTrinId: 'niveau-5-trin-5',
    tierId: 'niveau-5',
    title: 'Kontrol: tegn',
    description: '45 sek — ord med kolon, semikolon og store bogstaver.',
    goal: 'Bestå med mindst 90% nøjagtighed og 5 ord.',
    unlockedKeys: NIVEAU_5_UNLOCKED_KEYS,
    sprintWords: NIVEAU_5_SPRINT_WORDS,
    durationMs: 45_000,
    minWords: 5,
    minAccuracy: 0.9,
  },
  {
    id: 'mini-after-niveau-6-trin-3',
    afterTrinId: 'niveau-6-trin-3',
    tierId: 'niveau-6',
    title: 'Kontrol: flydighed',
    description: '45 sek — ord og sætninger med korrekt case.',
    goal: 'Bestå med mindst 90% nøjagtighed og 5 ord.',
    unlockedKeys: NIVEAU_6_UNLOCKED_KEYS,
    sprintWords: NIVEAU_6_SPRINT_WORDS,
    durationMs: 45_000,
    minWords: 5,
    minAccuracy: 0.9,
  },
];

const MINI_MAP = new Map(MINI_CHECKPOINTS.map((c) => [c.id, c]));
const AFTER_TRIN_MAP = new Map(MINI_CHECKPOINTS.map((c) => [c.afterTrinId, c]));

export function getMiniCheckpoint(id: string): MiniCheckpoint | undefined {
  return MINI_MAP.get(id);
}

export function getMiniCheckpointAfterTrin(
  trinId: string,
): MiniCheckpoint | undefined {
  return AFTER_TRIN_MAP.get(trinId);
}

export function miniCheckpointsInTier(
  tierId: CurriculumTierId,
): MiniCheckpoint[] {
  return MINI_CHECKPOINTS.filter((c) => c.tierId === tierId);
}
